import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Paper,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Grid,
  Slider,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
  ButtonGroup
} from '@mui/material';
import {
  Close as CloseIcon,
  QrCode as QrCodeIcon,
  Print as PrintIcon,
  Image as ImageIcon,
  TextFields as TextFieldsIcon,
  Download as DownloadIcon,
  FormatAlignLeftOutlined,
  FormatAlignCenterOutlined,
  FormatAlignRightOutlined,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { QRCodeCanvas } from 'qrcode.react';
import domtoimage from 'dom-to-image';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { buildShiftQrPayload, QrDirection } from '../../utils/shiftQr';
import GuideButton from '../common/guide/GuideButton';

// A5 boyutları (piksel olarak)
const a5DimensionsByOrientation = {
  portrait: { width: 420, height: 595 }, // Dikey A5
  landscape: { width: 595, height: 420 } // Yatay A5
};

interface VardiyaQrPrintModalProps {
  open: boolean;
  onClose: () => void;
  companyId?: string;
  branchId?: string;
}

const VardiyaQrPrintModal: React.FC<VardiyaQrPrintModalProps> = ({ open, onClose, companyId, branchId }) => {
  const { t, i18n } = useTranslation(['humanResources', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const hr = (key: string, defaultValue: string) => t(key, { ns: 'humanResources', defaultValue });
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [pageColor, setPageColor] = useState('#ffffff');
  const printRef = useRef<HTMLDivElement>(null);
  const [qrSize, setQrSize] = useState(120);
  // QR yönü: null => Tek QR (single/otomatik); 'in'|'out' => dual yönlü QR.
  const [qrDirection, setQrDirection] = useState<QrDirection>(null);
  const [position, setPosition] = useState({ x: 150, y: 150 });
  const [dragging, setDragging] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [showQrCode, setShowQrCode] = useState(false);
  
  // Dosya girişi için ref
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Metin düzenleme için state'ler
  const [textContent, setTextContent] = useState(hr('shiftQrDesigner.newText', 'New Text'));
  const [textFont, setTextFont] = useState('Roboto');
  const [textSize, setTextSize] = useState(16);
  const [textColor, setTextColor] = useState('#000000');
  const [textBold, setTextBold] = useState(false);
  const [textItalic, setTextItalic] = useState(false);
  
  // Yeniden boyutlandırma işlemleri için gerekli state'ler
  const [resizing, setResizing] = useState(false);
  const [resizeItemId, setResizeItemId] = useState<string | null>(null);
  const [resizeStartPoint, setResizeStartPoint] = useState({ x: 0, y: 0 });
  const [initialWidth, setInitialWidth] = useState(0);
  
  // Sayfa öğeleri
  const [pageItems, setPageItems] = useState<Array<{
    id: string;
    type: 'qr' | 'logo' | 'text';
    position: { x: number, y: number };
    size?: number;
    width?: number;
    content?: string;
    imageUrl?: string;
    selected?: boolean;
    font?: string;
    fontSize?: number;
    color?: string;
    bold?: boolean;
    italic?: boolean;
    textAlign?: 'left' | 'center' | 'right';
  }>>([]);

  // A5 boyutları - aktif yönlendirmeye göre
  const getActiveDimensions = () => a5DimensionsByOrientation[orientation];

  // Modal açıldığında lokalden kaydedilmiş düzeni yükle
  useEffect(() => {
    if (open) {
      loadSavedLayout();
    }
  }, [open, companyId, branchId]);

  // Düzeni localStorage'dan yükle
  const loadSavedLayout = () => {
    const storageKey = branchId ? `vardiyaQrLayout_branch_${branchId}` : `vardiyaQrLayout_company_${companyId}`;
    
    try {
      const savedLayout = localStorage.getItem(storageKey);
      if (savedLayout) {
        const layoutData = JSON.parse(savedLayout);
        setPageItems(layoutData.items || []);
        setOrientation(layoutData.orientation || 'portrait');
        setPageColor(layoutData.pageColor || '#ffffff');
        
        // QR kodu göster
        const hasQrCode = layoutData.items.some((item: any) => item.type === 'qr');
        setShowQrCode(hasQrCode);
      } else {
        // İlk defa açılıyorsa varsayılan değerleri ayarla
        setPageItems([]);
        setShowQrCode(false);
      }
    } catch (error) {
      console.error('Kaydedilmiş düzeni yükleme hatası:', error);
      setPageItems([]);
      setShowQrCode(false);
    }
  };

  // Düzeni localStorage'a kaydet
  const saveLayout = () => {
    const storageKey = branchId ? `vardiyaQrLayout_branch_${branchId}` : `vardiyaQrLayout_company_${companyId}`;
    
    try {
      const layoutData = {
        items: pageItems,
        orientation,
        pageColor
      };
      localStorage.setItem(storageKey, JSON.stringify(layoutData));
    } catch (error) {
      console.error('Düzen kaydetme hatası:', error);
    }
  };

  // Seçilen yöne göre QR içeriği (payload) üretir.
  // companyId varsa yeni etiketli format (yön dahil); yoksa legacy id'ye düşer.
  const buildQrContent = useCallback(
    (direction: QrDirection) =>
      companyId
        ? buildShiftQrPayload(companyId, { branchId, direction })
        : (branchId || ''),
    [companyId, branchId]
  );

  // QR Kod ekleme fonksiyonu
  const handleAddQrCode = useCallback(() => {
    if (!companyId && !branchId) {
      console.error("Geçerli şirket veya şube ID'si yok");
      return;
    }
    
    const dimensions = getActiveDimensions();
    
    // QR kodu pageItems'a ekle
    const newItem = {
      id: `qr-${Date.now()}`,
      type: 'qr' as const,
      position: { x: dimensions.width / 2, y: dimensions.height / 2 },
      size: qrSize,
      content: buildQrContent(qrDirection),
      selected: false
    };
    
    setPageItems(prev => [...prev, newItem]);
    setShowQrCode(true);
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  }, [branchId, companyId, qrSize, orientation, qrDirection, buildQrContent]);

  // QR yönünü değiştir: sayfadaki mevcut QR'ın içeriğini de anında güncelle.
  const handleChangeDirection = useCallback((direction: QrDirection) => {
    setQrDirection(direction);
    setPageItems(prev => prev.map(item =>
      item.type === 'qr' ? { ...item, content: buildQrContent(direction) } : item
    ));
    setTimeout(saveLayout, 100);
  }, [buildQrContent]);

  // Metin ekleme işlevi
  const handleAddText = () => {
    const newId = uuidv4();
    const dimensions = getActiveDimensions();
    
    setPageItems([...pageItems, {
      id: newId,
      type: 'text',
      position: { x: dimensions.width / 2, y: dimensions.height / 2 },
      content: hr('shiftQrDesigner.newText', 'New Text'),
      font: 'Roboto',
      fontSize: 16,
      color: '#000000',
      bold: false,
      italic: false,
      textAlign: 'left',
      width: 200, // Varsayılan genişlik
      selected: true
    }]);
    
    // Sadece yeni eklenen öğe seçili olsun
    setPageItems(prev => prev.map(item => ({
      ...item,
      selected: item.id === newId
    })));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Metin içeriğini güncelleme
  const handleTextContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newContent = e.target.value;
    setTextContent(newContent);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          content: newContent
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Yazı tipini güncelleme
  const handleFontChange = (event: SelectChangeEvent) => {
    const newFont = event.target.value;
    setTextFont(newFont);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          font: newFont
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Yazı boyutunu güncelleme
  const handleFontSizeChange = (event: Event, newValue: number | number[]) => {
    const newSize = newValue as number;
    setTextSize(newSize);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          fontSize: newSize
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Yazı rengini güncelleme
  const handleColorChange = (color: string) => {
    setTextColor(color);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          color: color
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Kalın yazıyı açıp kapatma
  const handleToggleBold = () => {
    const newBold = !textBold;
    setTextBold(newBold);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          bold: newBold
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // İtalik yazıyı açıp kapatma
  const handleToggleItalic = () => {
    const newItalic = !textItalic;
    setTextItalic(newItalic);
    
    // Seçili metni güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          italic: newItalic
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Hizalama işlevini ekle
  const handleTextAlign = (align: 'left' | 'center' | 'right') => {
    setPageItems(prev => prev.map(item => {
      if (item.selected && item.type === 'text') {
        return {
          ...item,
          textAlign: align
        };
      }
      return item;
    }));
    
    // Düzeni kaydet
    setTimeout(saveLayout, 100);
  };

  // Öğe seçildiğinde düzenleme alanlarını güncelle
  useEffect(() => {
    const selectedItem = pageItems.find(item => item.selected && item.type === 'text');
    if (selectedItem && selectedItem.type === 'text') {
      // Text ayarlarını güncelle
      setTextContent(selectedItem.content || hr('shiftQrDesigner.text', 'Text'));
      setTextFont(selectedItem.font || 'Roboto');
      setTextSize(selectedItem.fontSize || 16);
      setTextColor(selectedItem.color || '#000000');
      setTextBold(selectedItem.bold || false);
      setTextItalic(selectedItem.italic || false);
    }
  }, [pageItems]);

  // Logo ekleme işlevi
  const handleAddLogo = () => {
    // Dosya seçme dialogunu aç
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Dosya yükleme fonksiyonu
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Sadece resim dosyalarını kabul et
    if (!file.type.startsWith('image/')) {
      alert(hr('shiftQrDesigner.errors.imageOnly', 'Please upload image files only (PNG, JPG, JPEG).'));
      return;
    }
    
    // Resmi URL olarak oku
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageUrl = e.target?.result as string;
      const dimensions = getActiveDimensions();
      
      // Sayfaya logo ekle
      const newItem = {
        id: `logo-${Date.now()}`,
        type: 'logo' as const,
        position: { x: dimensions.width / 2, y: dimensions.height / 4 },
        size: 100,
        content: file.name,
        imageUrl,
        selected: false
      };
      
      setPageItems(prev => [...prev, newItem]);
      
      // Düzeni kaydet
      setTimeout(saveLayout, 100);
    };
    
    reader.readAsDataURL(file);
    
    // Input değerini sıfırla (aynı dosyayı tekrar seçebilmek için)
    event.target.value = '';
  };

  // Öğe seçme işlevi
  const handleSelectItem = (id: string) => {
    setPageItems(prev => prev.map(item => ({
      ...item,
      selected: item.id === id
    })));
  };

  // Seçili öğeyi silme işlevi
  const handleDeleteSelected = () => {
    setPageItems(prev => {
      const newItems = prev.filter(item => !item.selected);
      
      // QR kodu silindi mi kontrol et
      const hasQrCode = newItems.some(item => item.type === 'qr');
      if (!hasQrCode) {
        setShowQrCode(false);
      }
      
      // Düzeni kaydet
      setTimeout(saveLayout, 100);
      
      return newItems;
    });
  };

  // Sürükleme işlemleri
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>, itemId: string) => {
    // Olayı durdurup varsayılan eylemleri engelle
    e.preventDefault();
    e.stopPropagation();
    
    // Öğeyi seç
    handleSelectItem(itemId);
    
    // Öğeyi sürükleme moduna al
    setDragging(true);
    
    // Seçilen öğenin pozisyonunu bul
    const item = pageItems.find(i => i.id === itemId);
    if (!item) return;
    
    // Tıklama noktası ile öğe konumu arasındaki farkı hesapla
    setStartPoint({
      x: e.clientX - item.position.x,
      y: e.clientY - item.position.y
    });
  };

  // Boyutlandırma başlangıç fonksiyonu
  const handleResizeStart = (e: React.MouseEvent<HTMLDivElement>, itemId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Öğeyi seç
    handleSelectItem(itemId);
    
    // Yeniden boyutlandırma modunu aç
    setResizing(true);
    setResizeItemId(itemId);
    
    // Başlangıç noktasını kaydet
    setResizeStartPoint({
      x: e.clientX,
      y: e.clientY
    });
    
    // Öğenin başlangıç genişliğini kaydet
    const item = pageItems.find(i => i.id === itemId);
    if (item) {
      setInitialWidth(item.width || 200);
    }
  };

  // Fare hareketi fonksiyonu - hem sürükleme hem boyutlandırma için
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // Varsayılan eylemleri ve metin seçimini engelle
    e.preventDefault();
    e.stopPropagation();
    
    // Yeniden boyutlandırma modu
    if (resizing && resizeItemId) {
      const item = pageItems.find(i => i.id === resizeItemId);
      if (!item) return;
      
      // Hareket miktarını hesapla
      const dx = e.clientX - resizeStartPoint.x;
      
      // Minimum 100px, maksimum A5 genişliği kadar olabilir
      let newWidth = Math.max(100, Math.min(getActiveDimensions().width - 40, initialWidth + dx));
      
      // Sadece genişliği güncelle
      setPageItems(prev => prev.map(item => {
        if (item.id === resizeItemId) {
          return {
            ...item,
            width: newWidth
          };
        }
        return item;
      }));
      return;
    }
    
    // Normal sürükleme modu
    if (!dragging) return;
    
    // Seçili öğeyi bul
    const selectedItem = pageItems.find(item => item.selected);
    if (!selectedItem) return;
    
    // Yeni pozisyonu hesapla
    const newX = e.clientX - startPoint.x;
    const newY = e.clientY - startPoint.y;
    
    // Sınırlar için kullanılacak değerleri hesapla
    let minEdgeDistance = 20; // Minimum kenar mesafesi
    
    if (selectedItem.type === 'qr') {
      minEdgeDistance = (selectedItem.size || qrSize) / 2;
    } else if (selectedItem.type === 'logo') {
      minEdgeDistance = (selectedItem.size || 100) / 2;
    } else if (selectedItem.type === 'text') {
      minEdgeDistance = 50;
    }
    
    // A5 sayfa sınırları içinde kal
    const dimensions = getActiveDimensions();
    const boundedX = Math.max(minEdgeDistance, Math.min(dimensions.width - minEdgeDistance, newX));
    const boundedY = Math.max(minEdgeDistance, Math.min(dimensions.height - minEdgeDistance, newY));
    
    // Öğenin pozisyonunu güncelle
    setPageItems(prev => prev.map(item => {
      if (item.selected) {
        return {
          ...item,
          position: { x: boundedX, y: boundedY }
        };
      }
      return item;
    }));
  };

  // Fare bırakma fonksiyonu
  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    // Yeniden boyutlandırma modunu kapat
    if (resizing) {
      setResizing(false);
      setResizeItemId(null);
      saveLayout(); // Değişiklikleri kaydet
      return;
    }
    
    // Normal sürükleme işlemi
    if (dragging) {
      // Varsayılan eylemleri ve metin seçimini engelle
      e.preventDefault();
      e.stopPropagation();
      
      // Sürükleme modunu kapat
      setDragging(false);
      
      // Değişiklikleri kaydet
      saveLayout();
    }
  };

  // Yazdırma fonksiyonu
  const handlePrint = () => {
    if (!printRef.current) return;
    
    // Geçici olarak seçili öğeleri sakla
    const selectedItems = pageItems.filter(item => item.selected);
    
    try {
      // Seçimleri geçici olarak kaldır
      setPageItems(prevItems => prevItems.map(item => ({
        ...item,
        selected: false
      })));
      
      // DOM'un güncellenmesi için kısa bir gecikme ekle
      setTimeout(() => {
        if (!printRef.current) return;
        
        // dom-to-image ile A5 sayfasını JPG olarak oluştur
        domtoimage.toJpeg(printRef.current, { 
          quality: 0.95,
          bgcolor: pageColor,
          style: {
            transform: 'scale(1)',
            transformOrigin: 'top left',
            width: `${printRef.current.offsetWidth}px`,
            height: `${printRef.current.offsetHeight}px`
          }
        })
        .then(function (dataUrl) {
          // JPG'yi yazdır
          const printWindow = window.open('', '_blank');
          if (!printWindow) {
            alert(hr('shiftQrDesigner.errors.allowPopup', 'Please allow popup windows.'));
            
            // Seçimleri geri yükle
            setPageItems(prevItems => prevItems.map(item => ({
              ...item,
              selected: selectedItems.some(selItem => selItem.id === item.id)
            })));
            return;
          }
          
          const printCSS = `
            @page {
              size: ${orientation === 'portrait' ? 'A5 portrait' : 'A5 landscape'};
              margin: 0;
            }
            html, body {
              margin: 0;
              padding: 0;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: white;
            }
            img {
              width: ${orientation === 'portrait' ? '148mm' : '210mm'};
              height: ${orientation === 'portrait' ? '210mm' : '148mm'};
              object-fit: contain;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          `;
          
          // Yazdırma penceresi içeriğini oluştur
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>${branchId ? hr('shiftQrDesigner.branch', 'Branch') : hr('shiftQrDesigner.company', 'Company')} ${hr('shiftQrDesigner.qrCode', 'QR Code')}</title>
                <style>${printCSS}</style>
              </head>
              <body>
                <img src="${dataUrl}" alt="${hr('shiftQrDesigner.qrCodePage', 'QR Code Page')}" />
                <script>
                  window.onload = function() {
                    setTimeout(function() {
                      window.print();
                      window.close();
                    }, 500);
                  };
                </script>
              </body>
            </html>
          `);
          
          printWindow.document.close();
          
          // Seçimleri geri yükle
          setTimeout(() => {
            setPageItems(prevItems => prevItems.map(item => ({
              ...item,
              selected: selectedItems.some(selItem => selItem.id === item.id)
            })));
          }, 1000);
        })
        .catch(function (error) {
          console.error('Dom-to-image hatası:', error);
          alert(hr('shiftQrDesigner.errors.pageImageGeneration', 'An error occurred while generating the page image.'));
          
          // Seçimleri geri yükle
          setPageItems(prevItems => prevItems.map(item => ({
            ...item,
            selected: selectedItems.some(selItem => selItem.id === item.id)
          })));
        });
      }, 300);
    } catch (error) {
      console.error('Yazdırma hatası:', error);
      alert(hr('shiftQrDesigner.errors.printing', 'An error occurred during printing.'));
      
      // Hata durumunda seçimleri geri yükle
      setPageItems(prevItems => prevItems.map(item => ({
        ...item,
        selected: selectedItems.some(selItem => selItem.id === item.id)
      })));
    }
  };

  // Bilgisayara kaydetme fonksiyonu
  const handleSaveImage = () => {
    if (!printRef.current) return;
    
    // Geçici olarak seçili öğeleri sakla
    const selectedItems = pageItems.filter(item => item.selected);
    
    try {
      // Seçimleri geçici olarak kaldır
      setPageItems(prevItems => prevItems.map(item => ({
        ...item,
        selected: false
      })));
      
      // DOM'un güncellenmesi için kısa bir gecikme ekle
      setTimeout(() => {
        if (!printRef.current) return;
        
        // dom-to-image ile A5 sayfasını JPG olarak oluştur
        domtoimage.toJpeg(printRef.current, { 
          quality: 0.95,
          bgcolor: pageColor,
          style: {
            transform: 'scale(1)',
            transformOrigin: 'top left',
            width: `${printRef.current.offsetWidth}px`,
            height: `${printRef.current.offsetHeight}px`
          }
        })
        .then(function (dataUrl) {
          // İndirme bağlantısı oluştur
          const dirTag = qrDirection === 'in' ? '_IN' : qrDirection === 'out' ? '_OUT' : '';
          const link = document.createElement('a');
          link.download = `${branchId ? hr('shiftQrDesigner.branch', 'Branch') : hr('shiftQrDesigner.company', 'Company')}_QR_Code${dirTag}.jpg`;
          link.href = dataUrl;
          link.click();
          
          // Seçimleri geri yükle
          setPageItems(prevItems => prevItems.map(item => ({
            ...item,
            selected: selectedItems.some(selItem => selItem.id === item.id)
          })));
        })
        .catch(function (error) {
          console.error('Dom-to-image hatası:', error);
          alert(hr('shiftQrDesigner.errors.imageGeneration', 'An error occurred while generating the image.'));
          
          // Seçimleri geri yükle
          setPageItems(prevItems => prevItems.map(item => ({
            ...item,
            selected: selectedItems.some(selItem => selItem.id === item.id)
          })));
        });
      }, 300);
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      alert(hr('shiftQrDesigner.errors.imageSave', 'An error occurred while saving the image.'));
      
      // Hata durumunda seçimleri geri yükle
      setPageItems(prevItems => prevItems.map(item => ({
        ...item,
        selected: selectedItems.some(selItem => selItem.id === item.id)
      })));
    }
  };

  // Modal kapanırken güncel düzeni kaydet
  const handleCloseWithSave = () => {
    saveLayout(); // Mevcut tasarımı kaydet
    onClose(); // Modalı kapat
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseWithSave}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white',
        py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight="bold">{hr('shiftQrDesigner.title', 'Design Shift QR Code')}</Typography>
          <GuideButton guideKey="shifts.qrPrint" size="small" color="inherit" />
        </Box>
        <IconButton onClick={handleCloseWithSave} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
          {/* Sol Bölüm - A5 Tasarım Alanı */}
          <Box 
            sx={{ 
              flex: 2, 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'grey.100',
              overflow: 'auto'
            }}
          >
            <Paper
              ref={printRef}
              elevation={3}
              sx={{
                width: getActiveDimensions().width,
                height: getActiveDimensions().height,
                backgroundColor: pageColor,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {/* A5 sayfasına eklenen öğeler */}
              {pageItems.map(item => {
                if (item.type === 'qr') {
                  return (
                    <Box 
                      key={item.id}
                      sx={{ 
                        position: 'absolute',
                        top: item.position.y,
                        left: item.position.x,
                        transform: 'translate(-50%, -50%)',
                        cursor: dragging && item.selected ? 'grabbing' : 'grab',
                        backgroundColor: '#ffffff',
                        padding: '10px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        border: item.selected ? '2px solid #1976d2' : 'none',
                        zIndex: item.selected ? 10 : 1
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Metin seçimini engelle
                        handleMouseDown(e, item.id);
                      }}
                      className={item.selected ? "no-print" : ""}
                    >
                      <QRCodeCanvas 
                        value={item.content || ''}
                        size={item.size || qrSize}
                        level="H" // Yüksek hata düzeltme seviyesi
                        includeMargin={true}
                        bgColor="#ffffff"
                        fgColor="#000000"
                        id={`qr-${item.id}`}
                      />
                    </Box>
                  );
                } else if (item.type === 'text') {
                  return (
                    <Box
                      key={item.id}
                      sx={{ 
                        position: 'absolute',
                        top: item.position.y,
                        left: item.position.x,
                        transform: 'translate(-50%, -50%)',
                        cursor: dragging && item.selected ? 'grabbing' : 'grab',
                        padding: '10px',
                        background: item.selected ? 'rgba(255,255,255,0.9)' : 'transparent',
                        borderRadius: '4px',
                        border: item.selected ? '2px solid #1976d2' : 'none',
                        zIndex: item.selected ? 10 : 1,
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        width: `${item.width || 200}px`,
                        boxSizing: 'border-box',
                        '&:hover': {
                          outline: item.selected ? '1px dashed #1976d2' : 'none',
                        },
                        '&:hover::after': item.selected ? {
                          content: '""',
                          position: 'absolute',
                          right: '-4px',
                          bottom: '-4px',
                          width: '10px',
                          height: '10px',
                          backgroundColor: '#1976d2',
                          borderRadius: '50%',
                          cursor: 'nwse-resize'
                        } : {}
                      }}
                      onMouseDown={(e) => {
                        // Varsayılan metin seçme davranışını engelle
                        e.preventDefault();
                        
                        // Köşe noktasından yeniden boyutlandırma
                        const rect = e.currentTarget.getBoundingClientRect();
                        const isResizeHandle = 
                          e.clientX > rect.right - 15 && 
                          e.clientY > rect.bottom - 15;
                        
                        if (isResizeHandle) {
                          // Yeniden boyutlandırma modu aktif
                          handleResizeStart(e, item.id);
                        } else {
                          // Normal sürükleme modu
                          handleMouseDown(e, item.id);
                        }
                      }}
                      className={item.selected ? "no-print" : ""}
                    >
                      <Typography 
                        variant="body1"
                        sx={{
                          fontFamily: item.font || 'Roboto',
                          fontSize: `${item.fontSize || 16}px`,
                          color: item.color || '#000000',
                          fontWeight: item.bold ? 'bold' : 'normal',
                          fontStyle: item.italic ? 'italic' : 'normal',
                          whiteSpace: 'pre-wrap',
                          pointerEvents: 'none',
                          textAlign: item.textAlign || 'left',
                          width: '100%',
                          display: 'block'
                        }}
                      >
                        {item.content || 'Metin'}
                      </Typography>
                    </Box>
                  );
                } else if (item.type === 'logo') {
                  return (
                    <Box
                      key={item.id}
                      sx={{ 
                        position: 'absolute',
                        top: item.position.y,
                        left: item.position.x,
                        transform: 'translate(-50%, -50%)',
                        cursor: dragging && item.selected ? 'grabbing' : 'grab',
                        width: `${item.size}px`,
                        height: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        border: item.selected ? '2px solid #1976d2' : 'none',
                        zIndex: item.selected ? 10 : 1,
                        overflow: 'hidden'
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Metin seçimini engelle
                        handleMouseDown(e, item.id);
                      }}
                      className={item.selected ? "no-print" : ""}
                    >
                      {item.imageUrl ? (
                        <img 
                          src={item.imageUrl} 
                          alt={item.content || 'Logo'} 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'contain',
                            pointerEvents: 'none' // Resim etkileşimini engelle
                          }} 
                        />
                      ) : (
                        <ImageIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                      )}
                    </Box>
                  );
                }
                return null;
              })}
            </Paper>
          </Box>

          {/* Sağ Bölüm - Araçlar */}
          <Box sx={{ 
            flex: 1, 
            borderLeft: '1px solid', 
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            overflowY: 'auto'
          }}>
            {/* Sayfa Yönlendirmesi */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {hr('shiftQrDesigner.pageOrientation', 'Page Orientation')}
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup 
                  row 
                  value={orientation} 
                  onChange={(e) => setOrientation(e.target.value as 'portrait' | 'landscape')}
                >
                  <FormControlLabel 
                    value="portrait" 
                    control={<Radio />} 
                    label={hr('shiftQrDesigner.portrait', 'Portrait')}
                  />
                  <FormControlLabel 
                    value="landscape" 
                    control={<Radio />} 
                    label={hr('shiftQrDesigner.landscape', 'Landscape')}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Divider />

            {/* Seçili öğeyi silme butonu */}
            {pageItems.some(item => item.selected) && (
              <Box>
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  startIcon={<DeleteIcon />}
                  sx={{ py: 1.5, mb: 2 }}
                  onClick={handleDeleteSelected}
                >
                  {hr('shiftQrDesigner.deleteSelectedItem', 'Delete Selected Item')}
                </Button>
              </Box>
            )}

            {/* QR Türü (Tek QR / Giriş / Çıkış) */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {hr('shiftQrDesigner.qrType', tr('QR Türü', 'QR Type'))}
              </Typography>
              <ButtonGroup fullWidth size="small">
                <Button
                  variant={qrDirection === null ? 'contained' : 'outlined'}
                  onClick={() => handleChangeDirection(null)}
                >
                  {tr('Tek QR', 'Single QR')}
                </Button>
                <Button
                  variant={qrDirection === 'in' ? 'contained' : 'outlined'}
                  onClick={() => handleChangeDirection('in')}
                >
                  {tr('Giriş', 'In')}
                </Button>
                <Button
                  variant={qrDirection === 'out' ? 'contained' : 'outlined'}
                  onClick={() => handleChangeDirection('out')}
                >
                  {tr('Çıkış', 'Out')}
                </Button>
              </ButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {qrDirection === null
                  ? tr('Tek QR: giriş/çıkış otomatik (single vardiyalar).',
                       'Single QR: in/out is automatic (single-mode shifts).')
                  : tr('Çift QR modu: ayrı Giriş ve Çıkış QR kodları üretin (dual vardiyalar).',
                       'Dual QR mode: generate separate In and Out QR codes (dual-mode shifts).')}
              </Typography>
            </Box>

            <Divider />

            {/* QR Ekle Butonu */}
            <Box>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<QrCodeIcon />}
                sx={{ py: 1.5, mb: 2 }}
                onClick={handleAddQrCode}
                disabled={pageItems.some(item => item.type === 'qr')}
              >
                {hr('shiftQrDesigner.addQr', 'Add QR')}
              </Button>
              
              {pageItems.some(item => item.type === 'qr') && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    {hr('shiftQrDesigner.qrCodeSize', 'QR Code Size:')}
                  </Typography>
                  <Slider
                    min={80}
                    max={200}
                    step={10}
                    value={qrSize}
                    onChange={(_, value) => {
                      setQrSize(value as number);
                      // Seçili QR kodunun boyutunu güncelle
                      setPageItems(prev => prev.map(item => {
                        if (item.type === 'qr' && item.selected) {
                          return {
                            ...item,
                            size: value as number
                          };
                        }
                        return item;
                      }));
                      // Değişiklikleri kaydet
                      setTimeout(saveLayout, 100);
                    }}
                    valueLabelDisplay="auto"
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
            </Box>

            {/* Logo Ekle Butonu */}
            <Box>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<ImageIcon />}
                sx={{ py: 1.5, mb: 2 }}
                onClick={handleAddLogo}
              >
                {hr('shiftQrDesigner.addLogo', 'Add Logo')}
              </Button>
              
              {/* Gizli dosya girişi */}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleFileUpload}
              />
              
              {pageItems.some(item => item.type === 'logo' && item.selected) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    {hr('shiftQrDesigner.logoSize', 'Logo Size:')}
                  </Typography>
                  <Slider
                    min={50}
                    max={300}
                    step={10}
                    value={pageItems.find(item => item.type === 'logo' && item.selected)?.size || 100}
                    onChange={(_, value) => {
                      // Seçili logonun boyutunu güncelle
                      setPageItems(prev => prev.map(item => {
                        if (item.type === 'logo' && item.selected) {
                          return {
                            ...item,
                            size: value as number
                          };
                        }
                        return item;
                      }));
                      // Değişiklikleri kaydet
                      setTimeout(saveLayout, 100);
                    }}
                    valueLabelDisplay="auto"
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
            </Box>

            {/* Metin Ekle Butonu */}
            <Box>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<TextFieldsIcon />}
                sx={{ py: 1.5, mb: 2 }}
                onClick={handleAddText}
              >
                {hr('shiftQrDesigner.addText', 'Add Text')}
              </Button>
              
              {/* Metin Düzenleme Paneli */}
              {pageItems.some(item => item.type === 'text' && item.selected) && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {hr('shiftQrDesigner.textEditing', 'Text Editing')}
                  </Typography>
                  
                  {/* Metin İçeriği */}
                  <TextField
                    fullWidth
                    label={hr('shiftQrDesigner.text', 'Text')}
                    value={textContent}
                    onChange={handleTextContentChange}
                    margin="dense"
                    size="small"
                    multiline
                    rows={2}
                  />
                  
                  {/* Yazı Tipi */}
                  <FormControl fullWidth margin="dense" size="small">
                    <InputLabel>{hr('shiftQrDesigner.font', 'Font')}</InputLabel>
                    <Select
                      value={textFont}
                      label={hr('shiftQrDesigner.font', 'Font')}
                      onChange={handleFontChange}
                    >
                      <MenuItem value="Roboto">Roboto</MenuItem>
                      <MenuItem value="Arial">Arial</MenuItem>
                      <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                      <MenuItem value="Courier New">Courier New</MenuItem>
                      <MenuItem value="Georgia">Georgia</MenuItem>
                      <MenuItem value="Verdana">Verdana</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {/* Yazı Boyutu */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" gutterBottom>
                      {hr('shiftQrDesigner.fontSize', 'Font Size')}: {textSize}px
                    </Typography>
                    <Slider
                      min={8}
                      max={48}
                      step={1}
                      value={textSize}
                      onChange={handleFontSizeChange}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                  
                  {/* Metin Formatı ve Rengi */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2, gap: 1 }}>
                    {/* Yazı Hizalama */}
                    <Typography variant="caption" gutterBottom>
                      {hr('shiftQrDesigner.textAlignment', 'Text Alignment')}
                    </Typography>
                    <ButtonGroup size="small" fullWidth>
                      <Button 
                        onClick={() => handleTextAlign('left')}
                        variant={
                          pageItems.find(item => item.selected && item.type === 'text')?.textAlign === 'left' 
                            ? 'contained' 
                            : 'outlined'
                        }
                      >
                        <FormatAlignLeftOutlined />
                      </Button>
                      <Button 
                        onClick={() => handleTextAlign('center')}
                        variant={
                          pageItems.find(item => item.selected && item.type === 'text')?.textAlign === 'center' 
                            ? 'contained' 
                            : 'outlined'
                        }
                      >
                        <FormatAlignCenterOutlined />
                      </Button>
                      <Button 
                        onClick={() => handleTextAlign('right')}
                        variant={
                          pageItems.find(item => item.selected && item.type === 'text')?.textAlign === 'right' 
                            ? 'contained' 
                            : 'outlined'
                        }
                      >
                        <FormatAlignRightOutlined />
                      </Button>
                    </ButtonGroup>

                    {/* Kalın/İtalik butonları */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" gutterBottom>
                        {hr('shiftQrDesigner.textStyle', 'Text Style')}
                      </Typography>
                      <Box>
                        <IconButton 
                          size="small" 
                          onClick={handleToggleBold}
                          color={textBold ? "primary" : "default"}
                          sx={{ border: textBold ? '1px solid' : 'none' }}
                        >
                          <Typography sx={{ fontWeight: 'bold' }}>B</Typography>
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={handleToggleItalic}
                          color={textItalic ? "primary" : "default"}
                          sx={{ border: textItalic ? '1px solid' : 'none', ml: 1 }}
                        >
                          <Typography sx={{ fontStyle: 'italic' }}>I</Typography>
                        </IconButton>
                      </Box>
                    </Box>
                    
                    {/* Genişletilmiş Renk Paleti */}
                    <Typography variant="caption" gutterBottom display="block" sx={{ mt: 1 }}>
                      {hr('shiftQrDesigner.textColor', 'Text Color')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {[
                        '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
                        '#FF0000', '#FF6600', '#FFCC00', '#FFFF00', '#CCFF00', '#66FF00',
                        '#00FF00', '#00FF66', '#00FFCC', '#00FFFF', '#00CCFF', '#0066FF',
                        '#0000FF', '#6600FF', '#CC00FF', '#FF00FF', '#FF00CC', '#FF0066'
                      ].map(color => (
                        <Box
                          key={color}
                          onClick={() => handleColorChange(color)}
                          sx={{
                            width: 18,
                            height: 18,
                            bgcolor: color,
                            borderRadius: '2px',
                            cursor: 'pointer',
                            border: textColor === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                            '&:hover': { opacity: 0.8 }
                          }}
                        />
                      ))}
                      {/* Özel Renk Seçici */}
                      <Box
                        onClick={() => document.getElementById('custom-color-picker')?.click()}
                        sx={{
                          width: 18,
                          height: 18,
                          border: '1px dashed #aaa',
                          borderRadius: '2px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <span style={{ fontSize: '10px' }}>+</span>
                      </Box>
                      <input
                        type="color"
                        id="custom-color-picker"
                        value={textColor}
                        onChange={(e) => handleColorChange(e.target.value)}
                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            <Divider />

            {/* Arkaplan Rengi */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {hr('shiftQrDesigner.backgroundColor', 'Background Color')}
              </Typography>
              <Grid container spacing={1}>
                {['#ffffff', '#f5f5f5', '#fafafa', '#eeeeee', '#e0e0e0', '#FFEBEE', '#E3F2FD', '#E8F5E9', '#FFF8E1'].map((color) => (
                  <Grid item xs={4} key={color}>
                    <Box 
                      onClick={() => setPageColor(color)}
                      sx={{
                        width: '100%',
                        height: 40,
                        bgcolor: color,
                        border: pageColor === color ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<PrintIcon />} 
            onClick={handlePrint}
            sx={{ minWidth: '150px' }}
          >
            {hr('shiftQrDesigner.print', 'Print')}
          </Button>
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={<DownloadIcon />} 
            onClick={handleSaveImage}
            sx={{ minWidth: '150px' }}
          >
            {hr('shiftQrDesigner.saveToComputer', 'Save to Computer')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default VardiyaQrPrintModal; 